import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Clock, Globe } from 'lucide-react';
import { format } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { useToast } from '@/hooks/use-toast';
import { pb } from '@/lib/pocketbase';
import { useAuth } from '@/hooks/useAuth';
import { EmailService } from '@/services/emailService';
import { useTutorProfile } from '@/hooks/useTutorProfile';
import { getCountryByCode } from '@/utils/countries';

interface ScheduleSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ScheduleSessionModal = ({ isOpen, onClose }: ScheduleSessionModalProps) => {
  const { user } = useAuth();
  const { profile } = useTutorProfile();
  const { toast } = useToast();
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [subject, setSubject] = useState('');
  const [date, setDate] = useState<Date>();
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Get tutor's timezone from their profile or country
  const getTutorTimezone = () => {
    // If tutor has a specific timezone set in their profile, use that
    if (profile?.timezone) {
      return profile.timezone;
    }
    
    // Otherwise fall back to country-based timezone
    if (!profile?.country) return 'UTC';
    const country = getCountryByCode(profile.country);
    return country?.timezone || 'UTC';
  };

  const tutorTimezone = getTutorTimezone();

  // Get the local time display in tutor's timezone
  const getLocalTimeDisplay = () => {
    if (!startTime || !endTime || !date) {
      return '';
    }
    
    return `${startTime} - ${endTime} (${tutorTimezone})`;
  };

  const subjects = [
    'Mathematics', 'English', 'Science', 'History', 'Geography',
    'Physics', 'Chemistry', 'Biology', 'Computer Science', 'Art',
    'Music', 'Spanish', 'French', 'German', 'Economics'
  ];

  const timeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
    '18:00', '18:30', '19:00', '19:30', '20:00', '20:30'
  ];

  const validateTimeSlots = (start: string, end: string) => {
    const startIndex = timeSlots.indexOf(start);
    const endIndex = timeSlots.indexOf(end);
    return endIndex > startIndex;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || user.role !== 'tutor') {
      toast({
        title: "Error",
        description: "Only tutors can schedule sessions",
        variant: "destructive"
      });
      return;
    }

    if (!profile) {
      toast({
        title: "Error",
        description: "Tutor profile not found",
        variant: "destructive"
      });
      return;
    }

    // Validate required fields with detailed error messages
    const missingFields = [];
    if (!title) missingFields.push("Session Title");
    if (!subject) missingFields.push("Subject");
    if (!date) missingFields.push("Date");
    if (!startTime) missingFields.push("Start Time");
    if (!endTime) missingFields.push("End Time");
    
    if (missingFields.length > 0) {
      toast({
        title: "Missing Required Fields",
        description: `Please fill in the following required fields: ${missingFields.join(", ")}`,
        variant: "destructive"
      });
      return;
    }

    if (!validateTimeSlots(startTime, endTime)) {
      toast({
        title: "Error",
        description: "End time must be after start time",
        variant: "destructive"
      });
      return;
    }

    setIsCreating(true);
    
    try {
      // Get tutor's timezone
      const tutorTimezone = profile.timezone || getTutorTimezone();
      
      // Create booking record - using 'confirmed' status for scheduled sessions that are available for booking
      const bookingData = {
        tutorId: profile.id,
        studentId: '', // Will be filled when a student books
        subject,
        date: format(date, 'yyyy-MM-dd'),
        startTime,
        endTime,
        status: 'confirmed', // Use confirmed status for available scheduled sessions
        notes: description,
        paymentStatus: 'pending',
        price: profile.hourlyRate || 0,
        title,
        tutorTimezone: tutorTimezone // Store tutor's timezone
      };

      console.log('Creating scheduled session booking:', bookingData);
      
      const booking = await pb.collection('bookings').create(bookingData);
      
      console.log('Scheduled session created:', booking);

      // Send email notifications
      try {
        const tutorUser = profile;
        // For scheduled sessions, there's no student yet, so we pass null
        const studentUser = null; 
        const emailData = EmailService.formatBookingForEmail(booking, studentUser, tutorUser);
        
        console.log('📧 Sending scheduled session email notifications...');
        const emailResult = await EmailService.sendBookingNotification(emailData);
        
        if (emailResult.success) {
          console.log('✅ Email notifications sent successfully');
        } else {
          console.warn('⚠️ Email notifications failed:', emailResult.message);
        }
      } catch (emailError) {
        console.error('📧 Email notification error:', emailError);
        // Don't fail the booking if email fails - just log it
      }
      
      toast({
        title: "Success",
        description: "Scheduled session created! Students can now book this time slot.",
      });

      // Reset form
      setTitle('');
      setDescription('');
      setSubject('');
      setDate(undefined);
      setStartTime('');
      setEndTime('');
      
      onClose();
    } catch (error: any) {
      console.error('Error creating scheduled session:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create scheduled session",
        variant: "destructive"
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    if (!isCreating) {
      setTitle('');
      setDescription('');
      setSubject('');
      setDate(undefined);
      setStartTime('');
      setEndTime('');
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Schedule New Session</DialogTitle>
          <DialogDescription>
            Create a new available time slot that students can book
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Session Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Math Tutoring Session"
              required
            />
          </div>

          <div>
            <Label htmlFor="subject">Subject *</Label>
            <Select value={subject} onValueChange={setSubject} required>
              <SelectTrigger>
                <SelectValue placeholder="Select a subject" />
              </SelectTrigger>
              <SelectContent>
                {subjects.map((subj) => (
                  <SelectItem key={subj} value={subj}>
                    {subj}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                  type="button"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(date) => date < new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="startTime">Start Time *</Label>
              <Select value={startTime} onValueChange={setStartTime} required>
                <SelectTrigger>
                  <SelectValue placeholder="Start time" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((time) => (
                    <SelectItem key={time} value={time}>
                      {time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="endTime">End Time *</Label>
              <Select value={endTime} onValueChange={setEndTime} required>
                <SelectTrigger>
                  <SelectValue placeholder="End time" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.filter(time => !startTime || timeSlots.indexOf(time) > timeSlots.indexOf(startTime)).map((time) => (
                    <SelectItem key={time} value={time}>
                      {time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Timezone display */}
          {startTime && endTime && date && (
            <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-300">
                <Globe className="h-4 w-4" />
                <span className="font-medium">Session time:</span>
                <span>{getLocalTimeDisplay()}</span>
              </div>
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 ml-6">
                Times are shown in your country's timezone ({tutorTimezone})
              </p>
            </div>
          )}

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Session description, topics to cover, etc."
              rows={3}
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleClose}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isCreating}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Clock className="w-4 h-4 mr-2" />
              {isCreating ? 'Creating...' : 'Schedule Session'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleSessionModal;
